/**
 * The news card - Figma `Block/News` (node `48:1847`).
 *
 * One component covers the variants the CMS can actually produce:
 *
 * | Here | Figma | When |
 * |---|---|---|
 * | `plain`, `quarter` | `Quater` | no cover image, in a four-up row |
 * | `plain`, `half` | `Half size` | no cover image, in a two-up row |
 * | `plain` + `active` | `Active Half size` | the featured item, filled blue |
 * | `photo` | `Foto over size glass` | the article has a cover: a blurred dark panel over it |
 * | `banner` | *(none)* | the home page's pair - see below |

 *
 * **`banner` is not in the component set.** Every variant of `Block/News` is 444px tall,
 * but the home page's two cards (`3883:70253` and `3883:70411`) are local frames 225px
 * tall - a third of the block, against the 401px feature row's two thirds. So it is a
 * separate size rather than a shorter `half`: less room, and the design spends it
 * differently. The plain one drops the excerpt and puts the kind above the title with
 * the category chip opposite it; the one with a photo drops the chip and the date and
 * lays title and excerpt straight on the image.
 *
 * The glass variant is the one the home page actually uses for cards with a cover
 * (`6377:86962`), rather than the light-panel `Foto over size`.
 *
 * Not built: `Foto over size`, `Foto normal`, `Foto slim`, `Full-Foto over size`. They
 * are layout choices an editor cannot currently express, so they would be dead code
 * until a section calls for them.
 *
 * Two deliberate deviations:
 *
 * 1. **`min-h` instead of Figma's fixed 444px.** A Polish headline is routinely half
 *    again as long as the placeholder, and a fixed height would clip it.
 * 2. **Dates are formatted with `Intl`**, not as the mock-up's `07.Grudnia.2024` -
 *    see `formatDate` in lib/i18n.
 */
import { Link } from 'react-router-dom';
import { formatDate } from '../lib/i18n';
import { useSite } from '../lib/site-context';
import { Icon } from './ui/Icon';
import { Image, SIZES } from './ui/Image';
import type { ApiArticle } from '../lib/api';

export type ArticleCardSize = 'quarter' | 'half' | 'banner';

interface Props {
  article: ApiArticle;
  href: string;
  size?: ArticleCardSize;
  /** The filled-blue treatment Figma calls `Active Half size`. */
  active?: boolean;
}

/** Category chip. 20px of radius on a 30px-tall chip is a pill, hence `rounded-full`. */
function Chip({ label, className }: { label: string; className: string }) {
  return <span className={`inline-flex shrink-0 rounded-full border px-6 py-2 text-body-sm ${className}`}>{label}</span>;
}

export default function ArticleCard({ article, href, size = 'quarter', active = false }: Props) {
  const { locale, t } = useSite();
  const date = formatDate(locale, article.publishedDate);
  const cover = article.cover ?? null;

  // 444px is the card height in the design. `half` spans two grid columns from `sm` up,
  // which is the double-width first card on the news listing (Figma 6377:88454) and the
  // `Half size` variant elsewhere. `banner` spans the same two columns at 225px.
  const banner = size === 'banner';
  const shell = `group flex ${banner ? 'min-h-[225px]' : 'min-h-[444px]'} flex-col justify-between rounded-l transition ${
    size === 'half' || banner ? 'sm:col-span-2' : ''
  }`;

  // --- the home page's short pair (3883:70251) ------------------------------
  if (banner) {
    // 24px in from the sides, 28px from the top and bottom - the design's `News Item`
    // inset (`3883:70254`), which is not the 32px the taller cards use.
    if (cover) {
      return (
        <Link to={href} className={`${shell} relative overflow-hidden bg-grey-700 px-6 py-7 text-white`}>
          <Image
            media={cover}
            alt={article.cover?.alternativeText ?? ''}
            sizes={SIZES.half}
            className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
          {/* The design's photo is dark blue and its text sits straight on it. A scrim
              keeps that readable when an editor picks a pale photograph instead. */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/10" />
          <div className="relative flex flex-col gap-2.5">
            <h3 className="text-h5">{article.title}</h3>
            {article.excerpt ? <p className="line-clamp-3 max-w-[70%] text-body-sm">{article.excerpt}</p> : null}
          </div>
          <div className="relative flex justify-end">
            <Icon name="arrow-circle-right" size={48} className="transition group-hover:translate-x-1" />
          </div>
        </Link>
      );
    }

    return (
      <Link
        to={href}
        className={`${shell} px-6 py-7 ${active ? 'bg-blue-500 text-white' : 'bg-surface-muted hover:bg-surface-sunken'}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-4">
            <span className={`text-caption ${active ? 'text-blue-50' : 'text-ink-muted'}`}>
              {t(article.kind === 'blog' ? 'news.kindBlog' : 'news.kindNews')}
            </span>
            <h3 className={`text-h5 ${active ? 'text-white' : 'text-ink-strong'}`}>{article.title}</h3>
          </div>
          {article.category ? (
            <Chip label={article.category.name} className={active ? 'border-blue-300 text-blue-50' : 'border-grey-800 text-ink'} />
          ) : null}
        </div>
        <div className="flex items-center gap-2.5">
          <time className={`flex-1 text-body-sm ${active ? 'text-grey-100' : 'text-ink-muted'}`}>{date}</time>
          <Icon
            name="arrow-circle-right"
            size={48}
            className={`transition group-hover:translate-x-1 ${active ? 'text-white' : 'text-ink-strong'}`}
          />
        </div>
      </Link>
    );
  }

  // --- with a cover image: the glass panel over the photo --------------------
  if (cover) {
    return (
      <Link to={href} className={`${shell} relative overflow-hidden bg-grey-700 p-3`}>
        <Image
          media={cover}
          alt={article.cover?.alternativeText ?? ''}
          sizes={SIZES.quarter}
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="relative mt-auto flex w-full flex-col gap-5 rounded-s border border-white/20 bg-black/50 p-5 text-white backdrop-blur-[7px]">
          {article.category ? (
            <div className="flex flex-wrap gap-2.5">
              <span className="inline-flex h-7 shrink-0 items-center rounded-full border border-white/50 px-6 text-caption">{article.category.name}</span>
            </div>
          ) : null}
          <div className="flex flex-col gap-5">
            <h3 className="text-h4">{article.title}</h3>
            {article.excerpt ? <p className="text-body">{article.excerpt}</p> : null}
          </div>
          <div className="flex items-center gap-2.5">
            <time className="flex-1 text-body-sm">{date}</time>
            <Icon name="arrow-circle-right" size={48} className="transition group-hover:translate-x-1" />
          </div>
        </div>
      </Link>
    );
  }

  // --- no cover: the plain card, optionally the featured blue one ------------
  return (
    <Link to={href} className={`${shell} p-8 ${active ? 'bg-blue-500 text-white' : 'bg-surface-muted hover:bg-surface-sunken'}`}>
      <div className="flex w-full flex-col items-start gap-8">
        {article.category ? (
          <Chip label={article.category.name} className={active ? 'border-blue-300 text-blue-50' : 'border-grey-800 text-ink'} />
        ) : null}
        <div className={`flex w-full flex-col gap-6 ${active ? 'text-white' : 'text-ink-strong'}`}>
          <h3 className="text-h4">{article.title}</h3>
          {article.excerpt ? <p className="text-body">{article.excerpt}</p> : null}
        </div>
      </div>
      <div className="flex w-full items-center gap-2.5 pt-6">
        <time className={`flex-1 text-body-sm ${active ? 'text-grey-100' : 'text-ink-muted'}`}>{date}</time>
        <Icon
          name="arrow-circle-right"
          size={48}
          className={`transition group-hover:translate-x-1 ${active ? 'text-white' : 'text-ink-strong'}`}
        />
      </div>
    </Link>
  );
}
